import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Calendar, 
  Plus, 
  Bus, 
  ChevronLeft, 
  ChevronRight,
  Save,
  X,
  Search,
  Filter,
  User,
  Clock,
  MapPin,
  Euro,
  FileSpreadsheet,
  StickyNote,
  Phone,
  FileText
} from 'lucide-react';
import TripImport from './TripImport';
import { useModules } from '@/contexts/ModulesContext';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Fully isolated editable cell - manages its own editing state
const EditableCellIsolated = memo(function EditableCellIsolated({ 
  tripId, 
  field, 
  value, 
  type = 'text', 
  className = '',
  onSave 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef(null);

  // Sync with external value when not editing
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    if (localValue !== (value ?? '')) {
      onSave(tripId, field, localValue);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLocalValue(value ?? '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = (e) => {
    // Only save on blur if we're not clicking inside the same cell
    // This allows text selection without triggering save
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 150);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        data-testid={`edit-input-${field}`}
        className={`w-full px-1 py-0.5 text-xs border border-blue-400 rounded bg-white outline-none ${className}`}
        style={{ minWidth: type === 'number' ? '50px' : '60px' }}
      />
    );
  }

  return (
    <div
      onClick={handleStartEdit}
      data-testid={`editable-cell-${field}`}
      className={`cursor-text hover:bg-blue-50 px-1 py-0.5 rounded min-h-[24px] ${className}`}
    >
      {value || <span className="text-gray-300">—</span>}
    </div>
  );
});

export default function TripPlanner({ onClose }) {
  const { isModuleEnabled } = useModules();
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Selected day index (0-6 for Mon-Sun)
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const today = new Date().getDay();
    return today === 0 ? 6 : today - 1; // Convert Sun=0 to index 6, Mon=1 to index 0
  });
  
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  
  // Filters
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Quick add form
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    route_name: '',
    start_name: '',
    end_name: '',
    time_start: '08:00',
    bus_id: '',
    payment_amount: '',
    is_international: false,
  });

  // Import section visibility
  const [showImport, setShowImport] = useState(false);

  // Modified trips (for batch save)
  const [modifiedTrips, setModifiedTrips] = useState({});

  // Driver-bus assignments
  const [driverBusAssignments, setDriverBusAssignments] = useState({});

  // Note: Inline editing state is now managed inside EditableCellIsolated component

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tripsRes, usersRes, busesRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/api/trips`, getAuthHeaders()),
        axios.get(`${API}/api/users`, getAuthHeaders()),
        axios.get(`${API}/api/buses`, getAuthHeaders()),
        axios.get(`${API}/api/driver-bus-assignments`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setTrips(tripsRes.data);
      setDrivers(usersRes.data.filter(u => u.is_driver));
      setBuses(busesRes.data);
      
      // Convert assignments array to map { driver_id: bus_id }
      const assignmentsMap = {};
      assignmentsRes.data.forEach(a => {
        assignmentsMap[a.driver_id] = a.bus_id;
      });
      setDriverBusAssignments(assignmentsMap);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Open address in maps
  const openInMaps = (address) => {
    if (!address) return;
    
    // Check if it's coordinates
    const coordsMatch = address.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    const dmsMatch = address.match(/(\d+)°(\d+)'([\d.]+)"([NS])\s*(\d+)°(\d+)'([\d.]+)"([EW])/i);
    
    let url;
    if (coordsMatch) {
      // Decimal coordinates: 55.7558, 37.6173
      const lat = coordsMatch[1];
      const lng = coordsMatch[2];
      url = `https://www.google.com/maps?q=${lat},${lng}`;
    } else if (dmsMatch) {
      // DMS coordinates: 55°45'20.8"N 37°37'02.3"E
      const lat = parseFloat(dmsMatch[1]) + parseFloat(dmsMatch[2])/60 + parseFloat(dmsMatch[3])/3600;
      const lng = parseFloat(dmsMatch[5]) + parseFloat(dmsMatch[6])/60 + parseFloat(dmsMatch[7])/3600;
      const latSign = dmsMatch[4].toUpperCase() === 'S' ? -1 : 1;
      const lngSign = dmsMatch[8].toUpperCase() === 'W' ? -1 : 1;
      url = `https://www.google.com/maps?q=${lat * latSign},${lng * lngSign}`;
    } else {
      // Regular address
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    
    window.open(url, '_blank');
  };

  // Get week days
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedWeekStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  }, [selectedWeekStart]);

  // Current selected date
  const selectedDate = weekDays[selectedDayIndex];
  const selectedDateStr = selectedDate?.toISOString().split('T')[0];

  // Get trips for selected date with filters
  const filteredTrips = useMemo(() => {
    let result = trips
      .filter(t => t.date === selectedDateStr)
      .sort((a, b) => (a.time_start || '').localeCompare(b.time_start || ''));
    
    if (showOnlyUnassigned) {
      result = result.filter(t => !t.driver_id);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        (t.route_name || '').toLowerCase().includes(query) ||
        (t.bus_number || '').toLowerCase().includes(query) ||
        (t.driver_name || '').toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [trips, selectedDateStr, showOnlyUnassigned, searchQuery]);

  // Get stats for each day
  const getDayStats = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayTrips = trips.filter(t => t.date === dateStr);
    return {
      total: dayTrips.length,
      unassigned: dayTrips.filter(t => !t.driver_id).length
    };
  };

  // Navigate weeks
  const prevWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setSelectedWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setSelectedWeekStart(newStart);
  };

  const goToThisWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setSelectedWeekStart(new Date(today.setDate(diff)));
    setSelectedDayIndex(day === 0 ? 6 : day - 1);
  };

  // Assign driver
  const assignDriver = (tripId, driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    const assignedBusId = driverId ? driverBusAssignments[driverId] : null;
    const bus = assignedBusId ? buses.find(b => b.id === assignedBusId) : null;
    
    setTrips(prev => prev.map(t => {
      if (t.id === tripId) {
        const updates = {
          ...t,
          driver_id: driverId || null,
          driver_name: driver ? (driver.full_name || driver.username) : ''
        };
        // Auto-assign bus if driver has one assigned
        if (assignedBusId && bus) {
          updates.bus_id = assignedBusId;
          updates.bus_number = bus.license_plate;
        }
        return updates;
      }
      return t;
    }));
    
    const changes = { 
      driver_id: driverId || null, 
      driver_name: driver ? (driver.full_name || driver.username) : '' 
    };
    // Include bus in changes if auto-assigned
    if (assignedBusId && bus) {
      changes.bus_id = assignedBusId;
      changes.bus_number = bus.license_plate;
    }
    
    setModifiedTrips(prev => ({
      ...prev,
      [tripId]: { ...prev[tripId], ...changes }
    }));
  };

  // Assign bus to trip (auto-assign driver from settings if trip has no driver)
  const assignBus = (tripId, busId) => {
    const bus = buses.find(b => b.id === busId);
    const trip = trips.find(t => t.id === tripId);
    
    // Find driver assigned to this bus from settings
    const assignedDriverId = busId ? Object.entries(driverBusAssignments).find(([dId, bId]) => bId === busId)?.[0] : null;
    const assignedDriver = assignedDriverId ? drivers.find(d => d.id === assignedDriverId) : null;
    
    setTrips(prev => prev.map(t => {
      if (t.id === tripId) {
        const updates = {
          ...t,
          bus_id: busId || null,
          bus_number: bus ? bus.license_plate : ''
        };
        // Auto-assign driver if trip has no driver and bus has assigned driver
        if (!t.driver_id && assignedDriver) {
          updates.driver_id = assignedDriverId;
          updates.driver_name = assignedDriver.full_name || assignedDriver.username;
        }
        return updates;
      }
      return t;
    }));
    
    const changes = { 
      bus_id: busId || null, 
      bus_number: bus ? bus.license_plate : '' 
    };
    // Include driver in changes if auto-assigned
    if (!trip?.driver_id && assignedDriver) {
      changes.driver_id = assignedDriverId;
      changes.driver_name = assignedDriver.full_name || assignedDriver.username;
    }
    
    setModifiedTrips(prev => ({
      ...prev,
      [tripId]: { ...prev[tripId], ...changes }
    }));
    
    // Note: We don't save driver-bus assignment here - 
    // assignments are managed only in Settings -> Driver-Bus page
  };

  // Save all modified trips
  const saveChanges = async () => {
    const tripIds = Object.keys(modifiedTrips);
    if (tripIds.length === 0) {
      toast.info('Нет изменений для сохранения');
      return;
    }

    setSaving(true);
    let saved = 0;
    let errors = 0;

    for (const tripId of tripIds) {
      try {
        const trip = trips.find(t => t.id === tripId);
        if (trip) {
          await axios.put(`${API}/api/trips/${tripId}`, {
            ...trip,
            ...modifiedTrips[tripId]
          }, getAuthHeaders());
          saved++;
        }
      } catch (error) {
        errors++;
      }
    }

    setSaving(false);
    setModifiedTrips({});
    
    if (errors > 0) {
      toast.warning(`Сохранено: ${saved}, Ошибок: ${errors}`);
    } else {
      toast.success(`Сохранено ${saved} рейсов`);
    }
    
    fetchData();
  };

  // Quick create trip
  const createQuickTrip = async () => {
    if (!quickAddData.start_name && !quickAddData.end_name && !quickAddData.route_name) {
      toast.error('Укажите откуда и куда');
      return;
    }

    setSaving(true);
    try {
      const bus = buses.find(b => b.id === quickAddData.bus_id);
      await axios.post(`${API}/api/trips`, {
        date: selectedDateStr,
        time_start: quickAddData.time_start,
        route_name: quickAddData.route_name,
        start_location: { name: quickAddData.start_name },
        end_location: { name: quickAddData.end_name },
        bus_id: quickAddData.bus_id || null,
        bus_number: bus?.license_plate || '',
        payment_amount: parseFloat(quickAddData.payment_amount) || 0,
        driver_id: null,
        driver_name: '',
        status: 'planned',
        is_international: quickAddData.is_international || false
      }, getAuthHeaders());
      
      toast.success('Рейс создан');
      setQuickAddData({
        route_name: '',
        start_name: '',
        end_name: '',
        time_start: '08:00',
        bus_id: '',
        payment_amount: '',
        is_international: false,
      });
      setQuickAddOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Ошибка создания рейса');
    } finally {
      setSaving(false);
    }
  };

  // Handle cell save from isolated editable cells
  const handleCellSave = useCallback(async (tripId, field, value) => {
    const trip = trips.find(t => t.id === tripId);
    if (!trip) return;

    let updateData = { ...trip };
    
    if (field === 'admin_notes') updateData.admin_notes = value;
    else if (field === 'time_start') updateData.time_start = value;
    else if (field === 'route_name') updateData.route_name = value;
    else if (field === 'description') updateData.description = value;
    else if (field === 'start_name') {
      updateData.start_location = { name: value, address: value, lat: trip.start_location?.lat || null, lng: trip.start_location?.lng || null };
    } else if (field === 'end_name') {
      updateData.end_location = { name: value, address: value, lat: trip.end_location?.lat || null, lng: trip.end_location?.lng || null };
    } else if (field === 'payment_amount') updateData.payment_amount = parseFloat(value) || 0;
    else if (field === 'driver_salary') updateData.driver_salary = parseFloat(value) || 0;
    else if (field === 'contact_phone') updateData.contact_phone = value;
    else if (field === 'is_international') updateData.is_international = value;

    setTrips(prev => prev.map(t => t.id === tripId ? { ...t, ...updateData } : t));

    try {
      await axios.put(`${API}/api/trips/${tripId}`, updateData, getAuthHeaders());
    } catch (error) {
      toast.error('Ошибка сохранения');
    }
  }, [trips]);

  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const dayNamesFull = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
  const today = new Date().toISOString().split('T')[0];
  const hasChanges = Object.keys(modifiedTrips).length > 0;

  // Total stats for the week
  const weekStats = useMemo(() => {
    let total = 0;
    let unassigned = 0;
    weekDays.forEach(date => {
      const stats = getDayStats(date);
      total += stats.total;
      unassigned += stats.unassigned;
    });
    return { total, unassigned };
  }, [weekDays, trips]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white w-full max-w-5xl max-h-[95vh] rounded-2xl flex flex-col overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold">Планировщик рейсов</h2>
              <p className="text-xs text-white/80 hidden sm:block">
                Неделя: {weekDays[0].toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} — {weekDays[6].toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isModuleEnabled('trip_import') && (
              <Button 
                variant="ghost" 
                onClick={() => setShowImport(!showImport)}
                className={`text-white hover:bg-white/20 p-2 ${showImport ? 'bg-white/20' : ''}`}
                title="Импорт из Excel"
              >
                <FileSpreadsheet className="w-5 h-5" />
              </Button>
            )}
            {hasChanges && (
              <Button 
                onClick={saveChanges} 
                disabled={saving}
                size="sm"
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Save className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Сохранить</span>
                <span className="ml-1">({Object.keys(modifiedTrips).length})</span>
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/20 p-2">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Import Section */}
        {isModuleEnabled('trip_import') && showImport && (
          <div className="p-4 border-b bg-gray-50">
            <TripImport onImportComplete={() => { setShowImport(false); fetchData(); }} />
          </div>
        )}

        {/* Week Navigation + Day Tabs */}
        <div className="border-b bg-gray-50">
          {/* Week Navigation */}
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <Button variant="outline" size="sm" onClick={prevWeek} className="p-2">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                {weekDays[0].toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
              </span>
              <Button variant="outline" size="sm" onClick={goToThisWeek} className="text-xs px-2 py-1">
                Сегодня
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={nextWeek} className="p-2">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Day Tabs */}
          <div className="flex p-2 gap-1 overflow-x-auto">
            {weekDays.map((date, idx) => {
              const dateStr = date.toISOString().split('T')[0];
              const isToday = dateStr === today;
              const isSelected = idx === selectedDayIndex;
              const stats = getDayStats(date);
              
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDayIndex(idx)}
                  className={`flex-1 min-w-[70px] sm:min-w-[90px] p-2 sm:p-3 rounded-xl transition-all ${
                    isSelected 
                      ? 'bg-indigo-600 text-white shadow-lg scale-105' 
                      : isToday 
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                        : 'bg-white hover:bg-gray-100 border'
                  }`}
                >
                  <div className="text-xs font-medium">{dayNames[idx]}</div>
                  <div className="text-lg sm:text-xl font-bold">{date.getDate()}</div>
                  {stats.total > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        isSelected ? 'bg-white/30' : 'bg-gray-200'
                      }`}>
                        {stats.total}
                      </span>
                      {stats.unassigned > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          isSelected ? 'bg-orange-400' : 'bg-orange-500 text-white'
                        }`}>
                          {stats.unassigned}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3 bg-white border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              {dayNamesFull[selectedDayIndex]}, {selectedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
            </h3>
            <p className="text-sm text-gray-500">
              {filteredTrips.length} рейс(ов) {showOnlyUnassigned ? '(без водителя)' : ''}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none sm:w-48">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Поиск..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            
            {/* Filter */}
            <Button
              variant={showOnlyUnassigned ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlyUnassigned(!showOnlyUnassigned)}
              className={showOnlyUnassigned ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <Filter className="w-4 h-4 mr-1" />
              Без водителя
            </Button>
            
            {/* Add Trip */}
            <Button
              size="sm"
              onClick={() => setQuickAddOpen(!quickAddOpen)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>
        </div>

        {/* Quick Add Form */}
        {quickAddOpen && (
          <div className="p-3 bg-purple-50 border-b">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="w-20">
                <label className="text-xs text-gray-600 mb-1 block">Время</label>
                <Input
                  type="time"
                  value={quickAddData.time_start}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, time_start: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs text-gray-600 mb-1 block">Откуда</label>
                <Input
                  placeholder="Город/адрес отправления"
                  value={quickAddData.start_name}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, start_name: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs text-gray-600 mb-1 block">Куда</label>
                <Input
                  placeholder="Город/адрес назначения"
                  value={quickAddData.end_name}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, end_name: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="w-32 hidden lg:block">
                <label className="text-xs text-gray-600 mb-1 block">Маршрут (опц.)</label>
                <Input
                  placeholder="Название"
                  value={quickAddData.route_name}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, route_name: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="w-28">
                <label className="text-xs text-gray-600 mb-1 block">Автобус</label>
                <select
                  value={quickAddData.bus_id}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, bus_id: e.target.value }))}
                  className="w-full h-9 border rounded-lg px-2 text-sm"
                >
                  <option value="">—</option>
                  {buses.map(bus => (
                    <option key={bus.id} value={bus.id}>{bus.license_plate}</option>
                  ))}
                </select>
              </div>
              <div className="w-16">
                <label className="text-xs text-gray-600 mb-1 block">€</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={quickAddData.payment_amount}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, payment_amount: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="flex items-center gap-2 px-2 py-1 bg-purple-50 rounded-lg border border-purple-200">
                <input
                  type="checkbox"
                  id="is_international"
                  checked={quickAddData.is_international}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, is_international: e.target.checked }))}
                  className="w-4 h-4 accent-purple-600"
                />
                <label htmlFor="is_international" className="text-xs text-purple-700 font-medium cursor-pointer whitespace-nowrap">
                  🌍 Загран
                </label>
              </div>
              <Button onClick={createQuickTrip} disabled={saving} className="bg-purple-600 hover:bg-purple-700 h-9">
                {saving ? '...' : 'Создать'}
              </Button>
              <Button variant="outline" onClick={() => setQuickAddOpen(false)} className="h-9">
                ✕
              </Button>
            </div>
          </div>
        )}

        {/* Trips Table */}
        <div className="flex-1 overflow-auto">
          {filteredTrips.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Calendar className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Нет рейсов</p>
              <p className="text-sm">
                {showOnlyUnassigned ? 'Все рейсы распределены!' : 'Добавьте первый рейс'}
              </p>
              {!showOnlyUnassigned && (
                <Button 
                  onClick={() => setQuickAddOpen(true)} 
                  className="mt-4 bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить рейс
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr className="text-left text-[10px] text-gray-600 uppercase">
                  <th className="px-1 py-2 font-semibold">
                    <StickyNote className="w-3 h-3 inline mr-1" />Заметки
                  </th>
                  <th className="px-1 py-2 font-semibold w-14">
                    <Clock className="w-3 h-3 inline mr-1" />Время
                  </th>
                  <th className="px-1 py-2 font-semibold hidden xl:table-cell">
                    <MapPin className="w-3 h-3 inline mr-1" />Маршрут
                  </th>
                  <th className="px-1 py-2 font-semibold hidden 2xl:table-cell">
                    <FileText className="w-3 h-3 inline mr-1" />Описание
                  </th>
                  <th className="px-1 py-2 font-semibold">Откуда</th>
                  <th className="px-1 py-2 font-semibold">Куда</th>
                  <th className="px-1 py-2 font-semibold w-20 hidden md:table-cell">
                    <Bus className="w-3 h-3 inline mr-1" />Автобус
                  </th>
                  <th className="px-1 py-2 font-semibold w-28">
                    <User className="w-3 h-3 inline mr-1" />Водитель
                  </th>
                  <th className="px-1 py-2 font-semibold w-16 text-right">
                    <Euro className="w-3 h-3 inline mr-1" />Оплата
                  </th>
                  <th className="px-1 py-2 font-semibold hidden xl:table-cell">
                    <Phone className="w-3 h-3 inline mr-1" />Телефон
                  </th>
                  <th className="px-1 py-2 font-semibold w-10 text-center hidden lg:table-cell" title="Заграница">
                    🌍
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map((trip, idx) => {
                  const isModified = modifiedTrips[trip.id];
                  const hasDriver = trip.driver_id;
                  
                  return (
                    <tr 
                      key={trip.id}
                      className={`border-b transition-colors ${
                        isModified ? 'bg-green-50' :
                        hasDriver ? (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50') : 'bg-orange-50'
                      } hover:bg-gray-100`}
                    >
                      {/* 1. Заметки */}
                      <td className="px-1 py-1">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="admin_notes"
                          value={trip.admin_notes}
                          className="text-gray-600 truncate max-w-[120px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 2. Время */}
                      <td className="px-1 py-1">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="time_start"
                          value={trip.time_start}
                          type="time"
                          className="font-mono font-bold text-gray-800"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 3. Маршрут */}
                      <td className="px-1 py-1 hidden xl:table-cell">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="route_name"
                          value={trip.route_name}
                          className="text-gray-800 truncate max-w-[100px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 4. Описание */}
                      <td className="px-1 py-1 hidden 2xl:table-cell">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="description"
                          value={trip.description}
                          className="text-gray-600 truncate max-w-[100px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 5. Откуда */}
                      <td className="px-1 py-1">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="start_name"
                          value={trip.start_location?.name || trip.start_name}
                          className="text-gray-700 truncate max-w-[100px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 6. Куда */}
                      <td className="px-1 py-1">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="end_name"
                          value={trip.end_location?.name || trip.end_name}
                          className="text-gray-700 truncate max-w-[100px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 7. Автобус */}
                      <td className="px-1 py-1 hidden md:table-cell">
                        <select
                          value={trip.bus_id || ''}
                          onChange={(e) => assignBus(trip.id, e.target.value)}
                          className="w-full px-1 py-0.5 text-xs border rounded border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="">—</option>
                          {buses.map(bus => (
                            <option key={bus.id} value={bus.id}>{bus.license_plate}</option>
                          ))}
                        </select>
                      </td>
                      {/* 8. Водитель */}
                      <td className="px-1 py-1">
                        <select
                          value={trip.driver_id || ''}
                          onChange={(e) => assignDriver(trip.id, e.target.value)}
                          className={`w-full px-1 py-0.5 text-xs border rounded transition-colors ${
                            hasDriver 
                              ? 'border-green-300 bg-green-50 text-green-800' 
                              : 'border-orange-300 bg-orange-50 text-orange-800'
                          } focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                        >
                          <option value="">—</option>
                          {drivers.map(driver => (
                            <option key={driver.id} value={driver.id}>
                              {driver.full_name || driver.username}
                            </option>
                          ))}
                        </select>
                      </td>
                      {/* 9. Оплата */}
                      <td className="px-1 py-1 text-right">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="payment_amount"
                          value={trip.payment_amount || 0}
                          type="number"
                          className="font-semibold text-green-600 text-right"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 10. Телефон */}
                      <td className="px-1 py-1 hidden xl:table-cell">
                        <EditableCellIsolated
                          tripId={trip.id}
                          field="contact_phone"
                          value={trip.contact_phone}
                          className="text-gray-600 truncate max-w-[100px]"
                          onSave={handleCellSave}
                        />
                      </td>
                      {/* 11. Заграница */}
                      <td className="px-1 py-1 text-center hidden lg:table-cell">
                        <input
                          type="checkbox"
                          checked={trip.is_international || false}
                          onChange={(e) => handleCellSave(trip.id, 'is_international', e.target.checked)}
                          className="w-4 h-4 accent-purple-600 cursor-pointer"
                          title={trip.is_international ? 'Заграница' : 'Литва'}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-4">
            <span className="text-gray-600">
              Неделя: <strong>{weekStats.total}</strong> рейсов
            </span>
            {weekStats.unassigned > 0 && (
              <span className="text-orange-600">
                <strong>{weekStats.unassigned}</strong> без водителя
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-gray-500">
            <span>Водителей: {drivers.length}</span>
            <span>Автобусов: {buses.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
